import { FC, RefObject, useEffect, useCallback, useState } from 'react';
import { useRouter } from 'next/router';

import { ControlledDialog } from '@zoopla-eng/zds-components/dialog';
import { ButtonRight } from '@zoopla-eng/zds-components/button';
import { ArrowRightMedium } from '@zoopla-eng/zds-components/icons';
import { EventNames } from '@zoopla/tracking';

import { trackingHandler } from './tracking/estimate.tracking';
import { isValidEstimate } from './utils/estimates';
import Estimate from './Estimate/Estimate';
import PreviewGraph from './PreviewGraph/PreviewGraph';
import Modal from './Modal/Modal';

import { SaleEstimate, RentEstimate, Maybe } from '../graphql.generated';
import { useSaleEstimatesLazyQuery } from './Estimates.query.generated';
import { fontGroups } from '@zoopla-eng/zds-components/themes/font-groups';
import {
    estimateTitleStyle,
    noEstimateStyle,
    noEstimateTextStyle,
    styledEstimatesContainerStyle,
    styledEstimatesStyle,
    styledGraphContainerStyle,
} from './Estimates.styles.css';

export const SEE_MORE_DETAILS_LINK = 'See inside the estimate';

export type EstimatesProps = {
    saleEstimate?: Maybe<SaleEstimate>;
    rentEstimate?: Maybe<RentEstimate>;
    hideExplainerLink?: boolean;
    openAuthModal?: (
        triggerOrigin: RefObject<HTMLElement> | null,
        eventLabel: string
    ) => void;
    loginWallEnabled?: boolean;
    fullAddress?: string;
    isLoggedIn?: boolean;
    uprn?: Maybe<string> | undefined;
    isModalOpen: boolean;
    setIsModalOpen: (shouldOpenModal: boolean) => void;
};

const Estimates: FC<React.PropsWithChildren<EstimatesProps>> = ({
    saleEstimate,
    rentEstimate,
    openAuthModal,
    hideExplainerLink = false,
    loginWallEnabled = false,
    fullAddress = '',
    isLoggedIn,
    uprn,
    isModalOpen,
    setIsModalOpen,
}) => {
    const [saleEstimates, updateSaleEstimates] = useState<
        { builtAt?: string | null; currentPrice: number }[]
    >([]);
    const [deepLinkModalShown, setDeepLinkModalShown] = useState(false);
    const { asPath, query } = useRouter();

    const openModalFromDeepLink = Number(query['interactive-graph']) || 0;
    const hasSaleEstimates = saleEstimate && saleEstimates.length > 1;

    const handleModalToggle = useCallback(
        (open: boolean) => {
            setIsModalOpen(open);

            trackingHandler({
                path: asPath,
                eventAction: (open
                    ? EventNames.MODAL_SHOWN
                    : EventNames.MODAL_DISMISSED
                ).toLocaleLowerCase(),
                eventLabel: 'estimate modal',
                eventName: open
                    ? EventNames.MODAL_SHOWN
                    : EventNames.MODAL_DISMISSED,
            });
        },
        [asPath, setIsModalOpen]
    );

    const [saleEstimatesLazyQuery] = useSaleEstimatesLazyQuery({
        onError: function () {
            updateSaleEstimates([]);
            return;
        },
        onCompleted: (result) => {
            updateSaleEstimates(result?.property?.saleEstimates ?? []);
        },
        fetchPolicy: 'cache-and-network',
    });

    useEffect(() => {
        // We only render the preview graph and thus need the data if we have a
        // sale estimate
        if (saleEstimate)
            saleEstimatesLazyQuery({
                variables: {
                    uprn,
                },
            });
    }, [saleEstimatesLazyQuery, uprn, saleEstimate]);

    useEffect(() => {
        if (
            !deepLinkModalShown &&
            openModalFromDeepLink &&
            isLoggedIn &&
            hasSaleEstimates &&
            uprn
        ) {
            handleModalToggle(true);
            setDeepLinkModalShown(true);
        }
    }, [
        deepLinkModalShown,
        openModalFromDeepLink,
        isLoggedIn,
        hasSaleEstimates,
        uprn,
        handleModalToggle,
    ]);

    return (
        <div className={styledEstimatesContainerStyle}>
            <section
                className={styledEstimatesStyle}
                data-testid="property-estimate"
                id="property-estimate"
            >
                <h2 className={`${estimateTitleStyle} ${fontGroups.mug}`}>
                    Zoopla estimate
                </h2>
                {isValidEstimate(saleEstimate) ? (
                    <Estimate
                        {...saleEstimate}
                        type="sale"
                        hideExplainerLink={hideExplainerLink}
                        openAuthModal={openAuthModal}
                        loginWallEnabled={loginWallEnabled}
                    />
                ) : (
                    <div
                        className={noEstimateStyle}
                        data-testid="property-no-estimates"
                    >
                        <p
                            className={`${noEstimateTextStyle} ${fontGroups.ladle}`}
                        >
                            Zoopla estimate unavailable
                        </p>
                        <p className={fontGroups.knife}>
                            Unfortunately, we do not have enough data on this
                            property or similar homes in the area. We’re on a
                            mission to provide estimates for all properties in
                            the UK. Check back in a few months to see if we have
                            an estimate for this property.
                        </p>
                    </div>
                )}
            </section>

            {hasSaleEstimates && uprn && (
                <>
                    <div className={styledGraphContainerStyle}>
                        <ButtonRight
                            category="tertiary"
                            Icon={ArrowRightMedium}
                            onClick={() => handleModalToggle(true)}
                        >
                            {SEE_MORE_DETAILS_LINK}
                        </ButtonRight>
                        <PreviewGraph
                            saleEstimates={saleEstimates}
                            obscure={!isLoggedIn}
                            isModalOpen={isModalOpen}
                            onClick={() => handleModalToggle(true)}
                        />
                    </div>

                    <ControlledDialog
                        variant="large"
                        mobileVariant="fullScreen"
                        label={fullAddress}
                        title={fullAddress}
                        isOpen={isModalOpen}
                        onOpenChange={handleModalToggle}
                    >
                        <Modal
                            saleEstimates={saleEstimates}
                            saleEstimate={saleEstimate}
                            rentEstimate={rentEstimate}
                            uprn={uprn}
                        />
                    </ControlledDialog>
                </>
            )}
        </div>
    );
};

export default Estimates;
